import React, { useState } from 'react';
import { View, ScrollView, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import { scale, notchBottom } from '@components/frame';
import { formatSinceTime } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import RadioButton from '@components/common/radioButton';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import { SmallLoading } from '@components/loading';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const Card = ({ C, selected, setSelected, fullName, endpoint, macAddress, createdOn, modifiedOn, id, customStyle, newItem = {} }) => {
  const onSelect = () => {
    setSelected(id);
  };

  const isSelected = id === selected;

  const isPrevious = Boolean(id);

  const RenderCompareLine = ({ current, old }) => {
    if (!isPrevious) {
      return <View style={{ flex: 1 }} />;
    }
    const isSame = current == old;
    return [
      <View style={{ flex: 1, height: StyleSheet.hairlineWidth, backgroundColor: C('Hairline/Light/No trans') }} key={'compare-line'} />,
      <View
        style={{
          backgroundColor: C('Gray/Light/6'),
          borderRadius: scale(4),
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
        }}
        key={'compare-label'}
      >
        <Text
          style={[
            font.Small,
            { color: C(isSame ? 'System/Light/Green' : 'System/Light/Red'), paddingHorizontal: scale(4), paddingVertical: scale(2) },
          ]}
        >
          {i18n.t(isSame ? 'Same' : 'Different')}
        </Text>
      </View>,
    ];
  };

  return (
    <TouchableElement
      onPress={() => {
        onSelect();
      }}
    >
      {({ boxStyle }) => (
        <View style={[boxStyle, { alignItems: 'center' }]}>
          <View
            style={[
              {
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                backgroundColor: C('Background/Light/Field'),
                paddingHorizontal: scale(12),
                paddingVertical: scale(10),
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                borderWidth: scale(3),
                borderColor: 'transparent',
                ...customStyle,
              },
              isSelected && {
                borderColor: C('Selected/Light/Primary'),
              },
            ]}
          >
            <RadioButton {...{ isSelected, onSelect }} />
            <View style={{ marginLeft: scale(12), flex: 1 }}>
              <View style={{ flexDirection: 'row', gap: scale(6), flex: 1, alignItems: 'center' }}>
                <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{fullName}</Text>
                <RenderCompareLine {...{ current: newItem.fullName, old: fullName }} />
              </View>
              <View style={{ flexDirection: 'row', gap: scale(6), flex: 1, alignItems: 'center', marginTop: scale(6) }}>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary'), marginTop: scale(6) }]}>IP: {endpoint || i18n.t('Unknown')}</Text>
                <RenderCompareLine {...{ current: newItem.endpoint, old: endpoint }} />
              </View>
              <View style={{ flexDirection: 'row', gap: scale(6), flex: 1, alignItems: 'center', marginTop: scale(6) }}>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>MAC: {macAddress || i18n.t('Unknown')}</Text>
                <RenderCompareLine {...{ current: newItem.macAddress, old: macAddress }} />
              </View>
              {isPrevious && (
                <>
                  <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary'), marginTop: scale(6) }]}>
                    Created on: {createdOn && formatSinceTime(createdOn)}
                  </Text>
                  <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>
                    Deleted on: {modifiedOn && formatSinceTime(modifiedOn)}
                  </Text>
                </>
              )}
            </View>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

const detectSelected = (list, current) => {
  if (list) {
    const exist = list.find(
      (item) =>
        item.fullName == current.fullName &&
        item.machineConnection.endpoint == current.endpoint &&
        item.machineConnection.macAddress == current.macAddress
    );
    if (exist && exist.machineId) {
      return exist.machineId;
    }
  }
  return null;
};

export default ({ C, data, addMachineAction, goBack, maxHeight }) => {
  const { fullName, endpoint, macAddress } = data;

  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState(detectSelected(data.historyMachines, data));

  const onSubmit = async () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    await addMachineAction({ ...data, machineId: selected });
    setIsLoading(false);
  };

  const headerHeight = scale(58);

  return (
    <View>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: headerHeight,
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement
              onPress={() => {
                goBack();
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15), flexDirection: 'row' }]}>
                  <View style={{ marginTop: scale(2) }}>
                    <Svg icon="chevronLeft" width={scale(11)} height={scale(17)} color="Link / Light / Primary" />
                  </View>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')), marginLeft: scale(7) }, font.Body]}>{i18n.t('Back')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
            <TouchableElement onPress={onSubmit}>
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  {isLoading ? (
                    <SmallLoading />
                  ) : (
                    <Text style={[{ color: C(pressHoverColor(isLoading ? 'Label/Light/Quinary' : 'Link/Light/Primary')) }, font.Headline]}>
                      {i18n.t('Done')}
                    </Text>
                  )}
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Same Machine?')}</Text>
        </View>
      </View>
      <View style={{ maxHeight: maxHeight - headerHeight }}>
        <ScrollView contentContainerStyle={{ paddingHorizontal: scale(20), paddingBottom: scale(18) + notchBottom }}>
          <View style={{ width: '100%', alignItems: 'center' }}>
            <View style={{ width: '100%' }}>
              <View style={{ marginTop: scale(22) }}>
                <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>
                  {i18n.t("Before you proceed, there was a machine similar to the one you're about to add. Would you like to:")}
                </Text>
              </View>
              <View style={{ marginTop: scale(28) }}>
                <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Add new machine with no prior history')}</Text>
              </View>
            </View>
            <View style={{ marginTop: scale(4), width: '100%' }}>
              <Card {...{ C, selected, setSelected, fullName, endpoint, macAddress, id: null }} />
            </View>
            <View style={{ marginTop: scale(28), marginBottom: scale(8), width: '100%' }}>
              <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Or select previously existing machine')}</Text>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary'), marginTop: scale(4) }]}>
                {i18n.t("If you opt for a previously existing machine, the new one you're adding will inherit the history of the selected machine.")}
              </Text>
            </View>
            <View style={{ width: '100%' }}>
              {data.historyMachines.map((item) => {
                return (
                  <Card
                    key={item.machineId}
                    {...{
                      C,
                      selected,
                      setSelected,
                      fullName: item.fullName,
                      endpoint: item.machineConnection.endpoint,
                      macAddress: item.machineConnection.macAddress,
                      createdOn: item.createdOn,
                      modifiedOn: item.modifiedOn,
                      id: item.machineId,
                      customStyle: { marginTop: scale(15) },
                      newItem: data,
                    }}
                  />
                );
              })}
            </View>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};
